import { Box, Stack, Typography, useTheme } from '@mui/material';

import { STAR_ICONS, STAR_ICON_SX, STAR_POSITIONS, formatRating, getStarTypes } from 'components/AutoJerryReviewsBadge/utils';
import { useAutoJerryReviews } from 'components/AutoJerryReviewsBadge/useAutoJerryReviews';
import DotLabel from 'components/DotLabel';
import PhoneCtaButton from 'components/PhoneCtaButton';
import { heroGradient, panelBorder, redColors } from 'theme/colors';

const LIGHT_TEXT_COLOR = 'typography.light';
const DIM_TEXT_COLOR = 'rgba(238,240,242,0.65)';
const GRADIENT_TEXT_SX = {
  background: heroGradient,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  color: 'transparent',
} as const;

interface StatRowProps {
  label: string;
  value: React.ReactNode;
}

const StatRow: React.FC<StatRowProps> = ({ label, value }: StatRowProps) => (
  <Stack
    direction='row'
    sx={{
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 1.6,
      borderBottom: panelBorder,
      '&:last-of-type': { borderBottom: 'none' },
    }}
  >
    <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'secondary.main', boxShadow: `0 0 8px ${redColors.light}` }} />
      <Typography variant='p3' sx={{ color: DIM_TEXT_COLOR }}>
        {label}
      </Typography>
    </Stack>
    <Typography
      component='div'
      sx={{ fontFamily: 'Space Grotesk, Inter, Roboto, sans-serif', fontWeight: 700, fontSize: 15, color: LIGHT_TEXT_COLOR }}
    >
      {value}
    </Typography>
  </Stack>
);

const HeroContent: React.FC = () => {
  const theme = useTheme();
  const { rating, reviewCount, updatedAt } = useAutoJerryReviews();

  return (
    <Stack sx={{ pt: { xs: 5, md: 7 }, pb: { xs: 3, md: 4 } }}>
      <Box
        display='grid'
        gridTemplateColumns='1.1fr 0.9fr'
        gap={{ xs: 5, md: 7 }}
        sx={{
          alignItems: 'flex-start',
          [theme.breakpoints.down('md')]: {
            gridTemplateColumns: '1fr',
          },
        }}
      >
        <Stack spacing={0}>
          <Box sx={{ mb: 2.5 }}>
            <DotLabel label='Autokorjaamo · Seppälänkangas, Jyväskylä' sx={{ letterSpacing: '0.04em' }} />
          </Box>
          <Typography variant='h1'>
            Jyväskylän <Box component='span' sx={GRADIENT_TEXT_SX}>Huoltoykköset</Box>
          </Typography>
          <Typography variant='p2b' sx={{ mt: 2, color: LIGHT_TEXT_COLOR }}>
            Luottamuksellinen autonhuollon kumppanisi!
          </Typography>
          <Typography variant='p2' sx={{ mt: 2.25, maxWidth: '46ch', color: DIM_TEXT_COLOR }}>
            Tervetuloa paremman autohuollon asiakkaaksi. Tarjoamme teille korkealaatuisia autonhuollon palveluita, jotka perustuvat yli
            kymmenen vuoden kokemukseen ja ammattitaitoon. Tarkoituksemme on auttaa teitä löytämään parhaat autonhuollon ratkaisut ja
            tarjota ne edullisesti.
          </Typography>
          <PhoneCtaButton phone='0452393591' label='Soita ja varaa aika' sx={{ mt: 4 }} />
        </Stack>
        <Box
          sx={{
            border: panelBorder,
            borderRadius: '18px',
            bgcolor: 'rgba(255,255,255,0.035)',
            backdropFilter: 'blur(6px)',
            px: 3,
            py: 0.5,
          }}
        >
          <StatRow label='Kokemus' value='10+ vuotta' />
          <StatRow
            label='Arvostelut'
            value={
              <Stack direction='row' spacing={0.75} sx={{ alignItems: 'center' }}>
                <Stack direction='row' spacing={0.25} aria-hidden='true'>
                  {getStarTypes(rating).map((starType, index) => {
                    const StarComponent = STAR_ICONS[starType];
                    const starPosition = STAR_POSITIONS[index];

                    return <StarComponent key={starPosition} sx={{ ...STAR_ICON_SX, fontSize: 14 }} />;
                  })}
                </Stack>
                <span title={updatedAt === null ? undefined : `Päivitetty ${updatedAt}`}>
                  {formatRating(rating)}/5 · {reviewCount}
                </span>
              </Stack>
            }
          />
          <StatRow label='Avoinna' value='Ma–Pe 8–16:30' />
          <StatRow label='Osoite' value='Kuormaajantie 6, Jyväskylä' />
        </Box>
      </Box>
    </Stack>
  );
};

export default HeroContent;
